import * as React from 'react'
import invariant from 'tiny-invariant'
import { defaultLanguage } from 'cf-i18n/constants'
import { Language } from 'codegen/types'

import { useHasActiveSubscription } from '@repo/cf-user/contexts/auth'
import { useHasAllAccess, useIsAuthPending, useIsLoggedIn } from './use-auth'
import {
  useForceIsAllAccess,
  useHeaderContext,
  useLocale,
  useTranslations,
} from '../contexts/header-config-context'
import type { NavItemType } from './use-menu-items'
import { buildCrafts3DMenuItem } from './menu-sections/crafts-3d'
import { buildFontsMenuItem } from './menu-sections/fonts'
import { buildImagesMenuItem } from './menu-sections/images'
import { buildLearnMenuItem } from './menu-sections/learn'
import { buildOtherMenuItems } from './menu-sections/other-items'
import { buildStudioMenuItem } from './menu-sections/studio'
import {
  DUTCH_CANCEL_SUBSCRIPTION_LABEL,
  DUTCH_CANCEL_SUBSCRIPTION_PATH,
} from '../utils/dutch-cancel-subscription'

export type NavItem = {
  id?: NavItemType
  name: string
  link?: string
  activePaths?: string[]
  target?: string
  icon?: React.ReactNode
  track?: () => void
  wrapItems?: boolean
  wrapTitle?: boolean
  title?: string
  trending?:
    | {
        title?: string
        image?: string
        imageLink?: string
        imageText?: string
        layout?: never
      }
    | {
        title?: string
        items: {
          icon?: React.ReactNode
          iconLink: string
          iconText: string
        }[]
        layout: 'horizontal'
      }
  isFootOption?: boolean
  subMenuItems?: NavItem[]
} & (
  | {
      primaryName?: string
      secondaryName: string
      secondarySubMenuItems: NavItem[]
    }
  | {
      primaryName?: never
      secondaryName?: never
      secondarySubMenuItems?: never
    }
)

const CF_URL = process.env.NEXT_PUBLIC_CF_URL
invariant(CF_URL, 'NEXT_PUBLIC_CF_URL is required in cf-menu/hooks/use-nav-items.tsx')

export const useNavItems = () => {
  const t = useTranslations()
  const lang = useLocale()
  const isDutch = lang === Language.Nl
  const { isHomepage, navItemsOverride } = useHeaderContext()
  const isAuthLoading = useIsAuthPending()
  const isLoggedIn = useIsLoggedIn()
  const hasAllAccessSubscription = useHasAllAccess()
  const forceIsAllAccess = useForceIsAllAccess()
  const hasActiveSubscription = useHasActiveSubscription()
  const langSegment = lang === defaultLanguage ? '' : `/${lang}`

  const navItems = React.useMemo(() => {
    // On homepage, navItemsOverride replaces these — skip building ~700 objects
    if (isHomepage && navItemsOverride?.length) {
      return []
    }

    const fontsMenuItem = buildFontsMenuItem(t)
    const imagesMenuItem = buildImagesMenuItem(t)
    const crafts3DMenuItem = buildCrafts3DMenuItem(t)
    const studioMenuItem = buildStudioMenuItem(t, langSegment)
    const learnMenuItem = buildLearnMenuItem(t)
    const otherItems = buildOtherMenuItems(t)

    const items: NavItem[] = [
      fontsMenuItem,
      imagesMenuItem,
      crafts3DMenuItem,
      ...otherItems,
      learnMenuItem,
      studioMenuItem,
    ]

    if (!isDutch && !forceIsAllAccess && !isAuthLoading && !hasActiveSubscription) {
      items.push({
        id: 'subscription',
        name: t('navigation.subscription'),
        link: '/subscription/',
      })
    }

    if (
      !forceIsAllAccess &&
      !isAuthLoading &&
      isLoggedIn &&
      hasActiveSubscription &&
      !hasAllAccessSubscription
    ) {
      items.push({
        id: 'upgrade',
        name: t('navigation.upgrade'),
        link: '/subscription/',
      })
    }

    if (isDutch) {
      items.push({
        id: 'cancel-subscription',
        name: DUTCH_CANCEL_SUBSCRIPTION_LABEL,
        link: DUTCH_CANCEL_SUBSCRIPTION_PATH,
      })
    }

    return items
  }, [
    t,
    langSegment,
    isDutch,
    isAuthLoading,
    isLoggedIn,
    hasActiveSubscription,
    hasAllAccessSubscription,
    forceIsAllAccess,
    isHomepage,
    navItemsOverride,
  ])

  if (isHomepage && navItemsOverride?.length) {
    return navItemsOverride
  }

  return navItems
}
